/**
 * Ce fichier permets de créer la map et d'initialiser avec les différentes paramétrés
 *
 * © Moustapha HENNAWI - Click and Take off - 2021
 */

/*-------------------------------------------------------------------------------------------*/
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Threebox } from 'threebox-plugin';
import { styleMapboxOutdoors, styleMapboxSatelliteStreets } from './mapStyles';
import { SwitcherBtnControl } from './switcher/switcherBtn';

import './switcher/switcher.css';
import { Switcher3D2DControl } from './switcher/switcher3D2DControl';
import { CenterControl } from './switcher/centerControl';
import SwitcherMenu from './switcher/SwitcherMenu';
import AddIcaoView from './AddIcaoView';
import { useLocalization } from '../common/LocalizationProvider';
import { mapImages } from './preloadImages';
import { selectGroupsClubLatLong, selectSession } from '../createSelectorUtils';

/*-------------------------------------------------------------------------------------------*/
const element = document.createElement('div');
element.style.width = '100%';
element.style.height = '100%';
element.style.boxSizing = 'initial';
element.style.bottom = 0;
element.style.top = 0;
element.style.position = 'absolute';
/*-------------------------------------------------------------------------------------------*/

/*-------------------------------------------------------------------------------------------*/

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
export const map = new mapboxgl.Map({
  container: element,
  minZoom: 2,
  maxZoom: 18,
  antialias: true,
  interpolate: true,
  zoom: 13,
  pitch: 0,
  bearing: 0,
  projection: 'mercator',
  interactive: true
});

// eslint-disable-next-line no-multi-assign
export const tb = (window.tb = new Threebox(map, map.getCanvas().getContext('webgl'), {
  realSunlight: false,
  enableSelectingObjects: false, // change this to false to disable 3D objects selection
  enableTooltips: false,
  terrain: true,
  sky: true,
  defaultLights: true
}));

/*-------------------------------------------------------------------------------------------*/

let ready = false;
const readyListeners = new Set();

const addReadyListener = (listener) => {
  if (typeof listener === 'function') {
    readyListeners.add(listener);
    listener(ready);
  }
  return () => {
    readyListeners.delete(listener);
  };
};

/**
 * Removes a listener from the set of ready listeners.
 * @param {Function} listener - The listener function to be removed.
 * @returns {boolean} - True if the deletion was successful, false otherwise.
 */
const removeReadyListener = (listener) => {
  if (typeof listener === 'function') {
    try {
      if (readyListeners.has(listener)) {
        readyListeners.delete(listener);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error removing ready listener:', error);
      return false;
    }
  } else {
    return false;
  }
};

const updateReadyValue = (value) => {
  ready = value;
  readyListeners.forEach((listener) => listener(value));
};

const initMap = () =>
  new Promise((resolve, reject) => {
    if (ready) {
      resolve();
      return;
    }
    if (map && typeof map.hasImage === 'function' && typeof map.addImage === 'function') {
      if (!map.hasImage('plane') && Object.keys(mapImages).length > 0) {
        try {
          Object.entries(mapImages).forEach(([key, value]) => {
            map.addImage(key, value, {
              pixelRatio: 3.5
            });
          });
        } catch (error) {
          reject(error);
          return;
        }
      }
      updateReadyValue(true);
    }
    resolve();
  });

const scaleControl = new mapboxgl.ScaleControl();
map.addControl(scaleControl);

map.addControl(
  new mapboxgl.NavigationControl({
    showCompass: true
  }),
  'top-left'
);

map.addControl(new Switcher3D2DControl({ minpitchzoom: 11 }), 'top-left');

const centerControl = new CenterControl();
map.addControl(centerControl, 'top-left');

const controlGeocoder = new MapboxGeocoder({
  accessToken: mapboxgl.accessToken,
  mapboxgl,
  language: 'fr',
  collapsed: true
});
map.addControl(controlGeocoder);

const switcher = new SwitcherBtnControl(
  () => updateReadyValue(false),
  () => {
    map.once('styledata', () => {
      const waiting = () => {
        if (!map.loaded()) {
          setTimeout(waiting, 33);
        } else {
          initMap();
        }
      };
      waiting();
    });
  }
);
map.addControl(switcher);

const fullScreenControl = new mapboxgl.FullscreenControl();
map.addControl(fullScreenControl, 'bottom-right');

map.on('dataloading', () => {
  window.dispatchEvent(new Event('resize'));
  // map.resize();
});

/*-------------------------------------------------------------------------------------------*/
Map.propTypes = {
  children: PropTypes.node,
  disableMapInteraction: PropTypes.bool
};

function Map({ children, disableMapInteraction }) {
  const containerEl = useRef(null);
  const { language } = useLocalization();
  const [mapReady, setMapReady] = useState(false);

  const session = useSelector(selectSession);
  const groupsLocation = useSelector(selectGroupsClubLatLong);

  useEffect(() => {
    if (groupsLocation.length !== 0) {
      map.setCenter(groupsLocation);
    }
  }, [groupsLocation]);

  useLayoutEffect(() => {
    const currentEl = containerEl.current;
    currentEl.appendChild(element);
    return () => {
      currentEl.removeChild(element);
    };
  }, [containerEl]);

  useEffect(() => {
    switcher.initStyles(
      [
        {
          id: 'mapboxOutdoors',
          title: 'Mapbox Outdoors',
          uri: styleMapboxOutdoors()
        },
        {
          id: 'mapboxSatellite',
          title: 'Mapbox Satellite',
          uri: styleMapboxSatelliteStreets()
        }
      ],
      session.map === undefined ? 'Mapbox Satellite' : session.map
    );
    centerControl.updateStyles();
  }, [session.map]);

  useEffect(() => {
    if (mapReady) {
      map.setLayoutProperty('country-label', 'text-field', ['get', `name_${language}`]);

      if (map.hasControl(controlGeocoder)) {
        map.removeControl(controlGeocoder);
      }
      controlGeocoder.setLanguage(language);
      map.addControl(controlGeocoder);
    }

    return () => {
      if (map.hasControl(controlGeocoder)) {
        map.removeControl(controlGeocoder);
      }
    };
  }, [language, mapReady]);

  useEffect(() => {
    if (disableMapInteraction) {
      map.scrollZoom.disable();
      map.dragPan.disable();
      map.boxZoom.disable();
      map.keyboard.disable();
      map.doubleClickZoom.disable();
      map.touchZoomRotate.disable();
      if (map.hasControl(fullScreenControl)) {
        map.removeControl(fullScreenControl);
      }
    } else {
      map.scrollZoom.enable();
      map.dragPan.enable();
      map.boxZoom.enable();
      map.keyboard.enable();
      map.doubleClickZoom.enable();
      map.touchZoomRotate.enable();
      if (!map.hasControl(fullScreenControl)) {
        map.addControl(fullScreenControl, 'bottom-right');
      }
    }

    return () => {
      if (map.hasControl(fullScreenControl)) {
        map.removeControl(fullScreenControl);
      }
    };
  }, [disableMapInteraction]);

  useEffect(() => {
    const listener = (ready) => setMapReady(ready);
    addReadyListener(listener);
    return () => {
      removeReadyListener(listener);
    };
  }, []);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        top: 0,
        bottom: 0,
        position: 'absolute'
      }}
      ref={containerEl}
      id="map"
    >
      {mapReady && children}
      {mapReady && <SwitcherMenu switcher={switcher} />}
      {mapReady && <AddIcaoView />}
    </div>
  );
}

export default Map;
